import React,{useState,useEffect,useContext} from 'react';
import { styled } from '@mui/material/styles';
import {
  AppBar,
  Typography,
  FormHelperText,
  TableCell,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  Backdrop,
  Paper,
} from '@mui/material';

import {Auth} from '../../Api/auth';
import {UserRole} from '../../ApplicationConfig/constants';
import {UserInfoContext} from '../../StateObjects/UserInfoContext';
import {DeleteUserDialog} from './AlertDialogs';
import EnhancedTable from './EnhancedTable';
import Snackbar from '../CommonComponents/Snackbar';

import { useMsal } from "@azure/msal-react";

const PREFIX = 'UsersList';

const classes = {
  root: `${PREFIX}-root`,
  vettingContainerTitle: `${PREFIX}-vettingContainerTitle`,
  vettingSection: `${PREFIX}-vettingSection`,
  vettingRow: `${PREFIX}-vettingRow`,
  vettingColumn: `${PREFIX}-vettingColumn`,
  dialogFooter: `${PREFIX}-dialogFooter`,
  table: `${PREFIX}-table`,
  button: `${PREFIX}-button`,
  appBarHeader: `${PREFIX}-appBarHeader`,
  backdrop: `${PREFIX}-backdrop`,
  paper: `${PREFIX}-paper`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.root}`]: {
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(1.5, 3),
    },
  },

  [`& .${classes.vettingContainerTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  [`& .${classes.vettingSection}`]: {
    display: 'flex',
    flexFlow: 'column',
    padding: theme.spacing(3),
    overflowY: 'auto',
  },

  [`& .${classes.vettingRow}`]: {
    'display': 'flex',
    'margin': theme.spacing(1.5, 0),
    'flexFlow': 'row',
    'height': '100%',
    'justifyContent': 'center',
    'width': '100%',
    'alignItems': 'center',
    '&:first-child': {
      marginTop: 0,
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },

  [`& .${classes.vettingColumn}`]: {
    'display': 'flex',
    'flexDirection': 'column',
    'width': '100%',
    'justifyContent': 'center',
    'marginRight': theme.spacing(1),
    'height': '100%',
    '&:last-child': {
      marginRight: 0,
    },
  },

  [`& .${classes.dialogFooter}`]: {
    padding: theme.spacing(1.75, 3),
    borderTop: '1px solid',
    borderTopColor: theme.palette.divider,
  },

  [`& .${classes.table}`]: {
    minWidth: 650,
  },

  [`& .${classes.button}`]: {
    width: '25%',
  },

  [`& .${classes.appBarHeader}`]: {
    color: theme.palette.text.primary,
    background: 'white',
    position: 'static',
    top: 0,
    left: 'auto',
    width: '100%',
    padding: theme.spacing(2, 1, 1),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },

  [`& .${classes.paper}`]: {
    width: 200,
    maxHeight: 130,
    overflow: 'auto',
  }
}));

export default function UsersList(){

  const {userInfo} = useContext(UserInfoContext);
  const [tabValue, setTabValue] = useState(0);
  const defaultFormVals = {
    userAccount:"", 
    userAccountError:"",
    emailAddress:"", 
    emailAddressError:"",
    firstName: "",
    firstNameError: "",
    lastName: "",
    lastNameError: "",
    lang: 0,
    langError: "",
    role:0,
    roleError: "",
    researcherAdminAccount:0,
    analystAdminAccount:0
  }
  const [formValues, setFormValues] = useState({...defaultFormVals});
  const [open, setOpen] = useState({flag: false, deleteFlag:false, id: 0});
  const [rows, setRows] = useState([]);
  const [roleList, setRoleList] = useState({});
  const [languageList, setLanguageList] = useState({});
  const [projectList, setProjectList] = useState({});
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    severity: "success",
    message: ""
  });
  const [backdropOpen, setBackdropOpen] = React.useState(false);
  

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    let temp={...open}
    temp.flag=false
    setOpen(temp);
  };
  
  const handleValueChange = (e) => {
    let temp = {...formValues}
    temp[e.target.name] = e.target.value;
    if(e.target.name==="role" && e.target.value!==UserRole.Admin){
      temp.researcherAdminAccount=0;
      temp.analystAdminAccount=0;
    }
    setFormValues(temp);
  };

  const trimFormValues=()=>{
    return {...formValues, 
      userAccount:formValues.userAccount.trim(),
      emailAddress:formValues.emailAddress.trim(),
      firstName:formValues.firstName.trim(),
      lastName:formValues.lastName.trim()
    };
  };

  const {instance}=useMsal();

  useEffect(()=> {
    setBackdropOpen(true);
    Auth.authorizedFetch("/api/initManageUsers",{},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      
      return response.json();
    })
    .then(data => {
      setRoleList(data.roles);
      setLanguageList(data.langs);

      let res = {};
      data.projects.forEach(k=>{
          res[k.id]=k;
      });
      setProjectList(res);
      
      setRows(data.users);
    })
    .finally(()=>{
      setBackdropOpen(false);
    });
  }, []);

  const getUsers=()=>{
    setBackdropOpen(true);
    Auth.authorizedFetch("/api/getUsers",{},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      
      return response.json();
    })
    .then(data => {
      setRows(data.data);
    })
    .finally(()=>{
      setBackdropOpen(false);
    });
  }

  function validateEmail(email) {
    var re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    return re.test(email);
  }
  
  const validateForm=(formValues)=>{
    let errFlag=false;
    let missingMessage="Missing value";
    let invalidMessage="Invalid Value";
    let errors = {
      userAccountError:"",
      emailAddressError:"",
      firstNameError: "",
      lastNameError: "",
      langError: "",
      roleError: ""
    }
    if(formValues.userAccount===""){
      errors.userAccountError=missingMessage;
      errFlag=true;
    }else if(!validateEmail(formValues.userAccount)){
      errors.userAccountError=invalidMessage;
      errFlag=true;
    }
    if(formValues.emailAddress===""){
      errors.emailAddressError=missingMessage;
      errFlag=true;
    }else if(!validateEmail(formValues.emailAddress)){
      errors.emailAddressError=invalidMessage;
      errFlag=true;
    }
    if(formValues.firstName===""){
      errors.firstNameError=missingMessage;
      errFlag=true;
    }
    if(formValues.lastName===""){
      errors.lastNameError=missingMessage;
      errFlag=true;
    }
    if(formValues.lang===0){
      errors.langError=missingMessage;
      errFlag=true;
    }
    if(formValues.role===0){
      errors.roleError=missingMessage;
      errFlag=true;
    }
    if(errFlag){
      setFormValues({...formValues, ...errors});
    }
    return errFlag
  }

  const addNewUser = ()=>{
    let trimmedValues = trimFormValues();
    if(validateForm(trimmedValues)){
      return;
    }
    Auth.authorizedFetch("/api/createNewUser", {method:"POST", body: JSON.stringify(trimmedValues), headers:{'Content-type':'application/json'}},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      setSnackbarState({open: true, severity: "success", message: "New user created!"});
      getUsers();
      handleClose();
    });
  }

  const handleNewOpen = () => {
    setOpen({...open, flag: true, id:0});
    setFormValues({...defaultFormVals});
  };

  const editUser = (id)=>{
    let trimmedValues = trimFormValues();
    if(validateForm(trimmedValues)){
      return;
    }
    trimmedValues.id=id;
    Auth.authorizedFetch("/api/editUser", {method:"POST", body: JSON.stringify(trimmedValues), headers:{'Content-type':'application/json'}},instance)
    .then((response)=> {
      if(!response.ok){
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
      setSnackbarState({open: true, severity: "success", message: "User edited!"});
      getUsers();
      handleClose();
    });
  }
  
  const handleEditOpen = (id) => {
    setOpen({...open, flag:true, id: id});
    rows.some((row)=> {
      if(row.id===id){
        const reAccount = rows.find(r=>r.adminAccount===id && r.roles.includes(UserRole.Researcher));
        const anAccount = rows.find(r=>r.adminAccount===id && r.roles.includes(UserRole.Analyst));
        setFormValues({...defaultFormVals,
          userAccount:row.userAccount, 
          emailAddress:row.emailAddress, 
          firstName:row.firstName, 
          lastName: row.lastName, 
          lang: row.language_id,
          role:row.roles[0],
          researcherAdminAccount:reAccount ? reAccount.id : 0,
          analystAdminAccount:anAccount ? anAccount.id : 0
        });
        return true;
      }
    });
  };

  const deleteUser = (id)=>{
    Auth.authorizedFetch("/api/deleteUser", {method:"POST", body:JSON.stringify({id:id})},instance)
    .then((response)=> {
      if(response.ok){
        setSnackbarState({open: true, severity: "success", message: "User deleted!"});
        getUsers();
      }else if(response.status===400){
        setSnackbarState({open: true, severity: "error", message: "Can't delete a user still associated with a request"});
      }else{
        setSnackbarState({open: true, severity: "error", message: response.statusText});
        throw Error(response.statusText)
      }
    });
  }

  const headCells = [
    {id: 'userAccount', label: "User Account", isOrderable: true},
    {id: 'emailAddress', label: "Email Address", isOrderable: true},
    {id: 'firstName', label: "Full Name", isOrderable: true},
    {id: 'language_code', label: "Language", isOrderable: true},
    {id: 'roles', label: "Roles", isOrderable: true},
    {id: 'projects', label: "Projects", isOrderable: true},
    {id: 'actions', label: "Actions", isOrderable: false}
  ];

  const getTableRow=(row)=>{
    return (
      <TableRow key={row.id}>
        <TableCell component="th" scope="row">
          {row.userAccount}
          {row.roles.includes(UserRole.Admin) && 
          rows.filter((r)=>r.adminAccount===row.id && r.roles.includes(UserRole.Researcher)).map((r, i) => (
            <div key={i}><b>researcher account:</b> {r.userAccount}</div>
          ))}
          {row.roles.includes(UserRole.Admin) && 
          rows.filter((r)=>r.adminAccount===row.id && r.roles.includes(UserRole.Analyst)).map((r, i) => (
            <div key={i}><b>analyst account:</b> {r.userAccount}</div>
          ))}
          {row.adminAccount &&
          rows.filter((r)=>row.adminAccount===r.id).map((r, i) => (
            <div key={i}><b>admin account:</b> {r.userAccount}</div>
          ))}
        </TableCell>
        <TableCell>{row.emailAddress}</TableCell>
        <TableCell>{row.firstName+" "+row.lastName}</TableCell>
        <TableCell>{row.language_code}</TableCell>
        <TableCell>{roleList[row.roles[0]]}</TableCell>
        <TableCell>
          <Paper className={classes.paper}>
            <ul style={{paddingLeft: 20}}>
              {row.projects.map((proj, i)=>
                <li key={i}>{projectList[proj].projectNumber}</li>
              )}
            </ul>
          </Paper>
        </TableCell>
        <TableCell>
          <Button onClick={()=>handleEditOpen(row.id)}>Modify</Button>
          {row.id === userInfo.key ?
            <Tooltip title="You can't delete yourself">
              <span><Button disabled>Delete</Button></span>
            </Tooltip>
            : row.hasRequests ?
            <Tooltip title="You can't delete a user that has requests">
              <span><Button disabled>Delete</Button></span>
            </Tooltip>
            :
            <Button onClick={()=>setOpen({...open, deleteFlag: true, id:row.id})}>Delete</Button>
          }
        </TableCell>
      </TableRow>
    );
  }

  return (
    <Root>
      <AppBar
        className={classes.appBarHeader}
        elevation={0}
      >
        <Typography variant="h6" component="h1">
          Manage users
        </Typography>
      </AppBar>
      <Button className={classes.button} variant="outlined" onClick={()=>handleNewOpen()}>Add new user</Button>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab key={0} id="role-tab-0" label="all" />
        {Object.keys(roleList).map((role, i)=>
          <Tab key={i} id={"role-tab-"+role} label={roleList[role]} />
        )}
      </Tabs>
      <EnhancedTable
        headCells={headCells}
        rows={rows.filter((row)=>tabValue===0 || row.roles.includes(tabValue))}
        getTableRow={getTableRow}
        resetPageCount={tabValue}
      />
      <Backdrop className={classes.backdrop} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog
        open={open.flag}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.root}
      >
        <DialogTitle id="alert-dialog-title" className={classes.vettingContainerTitle}>
          <Typography variant="h6" component="h2">
            {open.id === 0 ? "Add new user" : "Modify existing user"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className={classes.vettingSection}>
            <div className={classes.vettingRow}>
              <div className={classes.vettingColumn}>
                <TextField
                  autoFocus
                  id="userAccount"
                  name="userAccount"
                  label="User Account"
                  sx={{mt: 1}}
                  type="email"
                  required
                  fullWidth
                  onChange={handleValueChange}
                  value={formValues.userAccount}
                  error={Boolean(formValues.userAccountError)}
                  helperText={formValues.userAccountError}
                />
                <TextField
                  id="emailAddress"
                  name="emailAddress"
                  label="Email Address"
                  sx={{mt: 1}}
                  type="email"
                  required
                  fullWidth
                  onChange={handleValueChange}
                  value={formValues.emailAddress}
                  error={Boolean(formValues.emailAddressError)}
                  helperText={formValues.emailAddressError}
                />
                <TextField
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  sx={{mt: 1}}
                  type="text"
                  required
                  fullWidth
                  onChange={handleValueChange}
                  value={formValues.firstName}
                  error={Boolean(formValues.firstNameError)}
                  helperText={formValues.firstNameError}
                />
                <TextField
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  sx={{mt: 1}}
                  type="text"
                  required
                  fullWidth
                  onChange={handleValueChange}
                  value={formValues.lastName}
                  error={Boolean(formValues.lastNameError)}
                  helperText={formValues.lastNameError}
                />
                <FormControl
                  required
                  variant="outlined"
                  fullWidth
                  sx={{mt: 2}}
                  margin="dense"
                  error={Boolean(formValues.langError)}
                >
                  <InputLabel id="lang-label">Language Preference</InputLabel>
                  <Select
                    id="lang"
                    name="lang"
                    label="Language Preference *"
                    labelId="lang-label"
                    onChange={handleValueChange}
                    value={formValues.lang}
                  >
                    {formValues.lang === 0 &&
                      <MenuItem key={0} value={0} disabled>None</MenuItem>
                    }
                    {Object.keys(languageList).map((lang, i)=>
                      <MenuItem key={i} value={parseInt(lang)}>{languageList[lang]}</MenuItem>
                    )}
                  </Select>
                  <FormHelperText>{formValues.langError}</FormHelperText>
                </FormControl>
                <FormControl
                  required
                  variant="outlined"
                  fullWidth
                  sx={{mt: 2}}
                  margin="dense"
                  error={Boolean(formValues.roleError)}
                >
                  <InputLabel id="role-label">Role</InputLabel>
                  <Select
                    id="role"
                    name="role"
                    label="Role *"
                    labelId="role-label"
                    onChange={handleValueChange}
                    value={formValues.role}
                    disabled={open.id===userInfo.key}
                  >
                    {formValues.role===0 &&
                      <MenuItem key={0} value={0} disabled>None</MenuItem>
                    }
                    {Object.keys(roleList).map((role, i)=>
                      <MenuItem key={i} value={parseInt(role)}>{roleList[role]}</MenuItem>
                    )}
                  </Select>
                  <FormHelperText>{formValues.roleError}</FormHelperText>
                </FormControl>
                {formValues.role===UserRole.Admin &&
                  <>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      sx={{mt: 2}}
                      margin="dense"
                    >
                      <InputLabel id="researcher-label">Researcher sub-account</InputLabel>
                      <Select
                        id="researcherAdminAccount"
                        name="researcherAdminAccount"
                        label="Researcher sub-account"
                        labelId="researcher-label"
                        onChange={handleValueChange}
                        value={formValues.researcherAdminAccount}
                      >
                        <MenuItem key={0} value={0}>None</MenuItem>
                        {rows.filter((r)=>r.roles.includes(UserRole.Researcher)).map((r, i) => (
                          <MenuItem key={i} value={r.id}>{r.userAccount}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      sx={{mt: 2}}
                      margin="dense"
                    >
                      <InputLabel id="researcher-label">Analyst sub-account</InputLabel>
                      <Select
                        id="analystAdminAccount"
                        name="analystAdminAccount"
                        label="Analyst sub-account"
                        labelId="analyst-label"
                        onChange={handleValueChange}
                        value={formValues.analystAdminAccount}
                      >
                        <MenuItem key={0} value={0}>None</MenuItem>
                        {rows.filter((r)=>r.roles.includes(UserRole.Analyst)).map((r, i) => (
                          <MenuItem key={i} value={r.id}>{r.userAccount}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                }
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogFooter}>
          <Button variant="outlined" onClick={handleClose}>Cancel</Button>
          {open.id === 0 &&
            <Button variant="outlined" onClick={addNewUser}>Add new user</Button>
          }
          {open.id !== 0 &&
            <Button variant="outlined" onClick={()=>editUser(open.id)}>Modify user</Button>
          }
        </DialogActions>
      </Dialog>
      <DeleteUserDialog 
        toggleDialog={()=>setOpen({...open, deleteFlag: false, id:0})}
        handleDelete={deleteUser}
        open={open.deleteFlag}
        userId={open.id}
      />
      <Snackbar 
        message={snackbarState.message}
        severity={snackbarState.severity}
        open={snackbarState.open}
        handleClose={()=>{setSnackbarState({...snackbarState, open: false, message: ""})}}
      />
    </Root>
  );
}